class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const targetElement = parsedHTML.querySelector(selector);
    
    if (targetElement) {
      return targetElement.innerHTML;
    }
    
    // Fallback: if selector not found, return the body's innerHTML
    // This handles cases where the server returns just the inner content
    return parsedHTML.body.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'CartDrawer-Upsell',
        section: 'cart-drawer',
        selector: '#CartDrawer-Upsell'
      }
    ];
  }
}


/**
 * Custom element to wrap Swiper carousel initialization
 * Automatically initializes Swiper when connected to DOM
 */
class SwiperCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
  }

  connectedCallback() {
    // Small delay to ensure DOM is fully ready
    setTimeout(() => {
      this.initializeSwiper();
    }, 100);
  }

  initializeSwiper() {
    if (this.swiperInstance || typeof Swiper === 'undefined') return;

    const slides = this.querySelectorAll('.swiper-slide');
    if (slides.length === 0) return;

    // Only initialize if there are slides
    this.swiperInstance = new Swiper(this, {
      slidesPerView: 1,
      spaceBetween: 16,
      grabCursor: true,
      watchOverflow: true,
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      },
      breakpoints: {
        750: {
          slidesPerView: 1,
          spaceBetween: 20,
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  refresh() {
    if (this.swiperInstance) {
      this.swiperInstance.update();
    } else {
      this.initializeSwiper();
    }
  }
}

customElements.define('swiper-carousel', SwiperCarousel);

/**
 * Custom element for cart upsell slider component
 * Manages the upsell product slider within the cart drawer
 */
class CartUpsell extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const swiperElement = this.querySelector('swiper-carousel');
    if (swiperElement) {
      // Trigger swiper initialization if it hasn't been done
      setTimeout(() => {
        if (!swiperElement.swiperInstance) {
          swiperElement.initializeSwiper();
        }
      }, 150);
    }
  }

  disconnectedCallback() {
    const swiperElement = this.querySelector('swiper-carousel');
    if (swiperElement && swiperElement.swiperInstance) {
      swiperElement.swiperInstance.destroy(true, true);
    }
  }

  refresh() {
    const swiperElement = this.querySelector('swiper-carousel');
    if (swiperElement) {
      swiperElement.refresh();
    }
  }
}

customElements.define('cart-upsell', CartUpsell);
customElements.define('cart-drawer-items', CartDrawerItems);
